import React, { PureComponent } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Label } from 'recharts';
import OilRing from './OilRing';
import moment from 'moment';
import { getNameForOil } from '../../../../common/Oils';
import { Form, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getNumberDisplayValue } from '../../../../utils/format';


const options = {
  oil: [
    {
      value: "Diesel",
      label: "Diislikütus",
      labelShort: "D"
    },
    {
      value: "DieselSpecial",
      label: "Erimärgistusega diislikütus",
      labelShort: "EDK"
    },
    {
      value: "HydrotreatedVegetableOil",
      label: "HVO-biokütus",
      labelShort: "HVO"
    },
    {
      value: "Petrol95",
      label: "Bensiin 95",
      labelShort: "95"
    },
    {
      value: "Petrol98",
      label: "Bensiin 98",
      labelShort: "98"
    }
  ]
};

const Effectiveness = ({ percentage }) => {
  let evaluation;

  if (percentage >= 5) {
    evaluation = { category: "Suurepärane", color: "#6D9F71" };
  } else if (percentage >= 1 && percentage < 5) {
    evaluation = { category: "Hea", color: "#D1A827" };
  } else if (percentage >= -1 && percentage < 1) {
    evaluation = { category: "Keskmine", color: "#CC6E3F" };
  } else {
    evaluation = { category: "Parandatav", color: "#C94444" };
  }

  return (
    <span style={{ color: evaluation.color, fontWeight: 600 }}>
      {evaluation.category}
    </span>
  );
};

interface Props {
  data: any;
}

interface State {
  filter: any;
  localData: any;
  lastDataRef: any;
}

export default class PriceHistoryChart extends PureComponent<Props, State> {

  constructor(props) {
    super(props);
    this.state = { filter: { oil: "Diesel", type: 0 }, localData: props.data || [], lastDataRef: props.data || [] };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    // Reset localData only if parent sends a new reference
    if (nextProps.data !== prevState.lastDataRef) {
      return {
        localData: nextProps.data,
        lastDataRef: nextProps.data
      };
    }
    return null;
  }

  handleFilterClick = (filter) => {
    if (this.state.filter.oil !== filter) {
      this.setState((prevState) => ({
        ...prevState,
        filter: {
          ...prevState.filter,
          oil: filter,
        },
      }));
    }
  };

  handleTableTypeChange = (type) => {
    if (this.state.filter.type !== type) {
      this.setState((prevState) => ({
        ...prevState,
        filter: {
          ...prevState.filter,
          type: type,
        },
      }));
    }
  };

  handleInputChange = (e: any, row: any) => {
    const { name, value } = e.target;

    // Regex to allow only digits and one optional dot
    if (/^\d*\.?\d*$/.test(value)) {
      this.setState((prevState) => ({
        ...prevState,
        localData: prevState.localData.map((item) =>
          item.oilRequestId === row.oilRequestId
            ? { ...item, [name]: value === '' ? '' : Number(value) }
            : item
        )
      }));
    }

  };

  render() {
    let filteredResults = (this.state.localData || []).filter(item => item?.type && item.type === this.state.filter.oil);
    filteredResults = filteredResults.map(item => ({
      ...item,
      endDatetime: moment(item.endDatetime).valueOf() // Convert date to timestamp
    }));

    return (
      <div className="pricesAndAmounts">
        <div className="ringsSection">
          {options.oil.map((option: any, index) => (
            <OilRing
              key={index}
              letters={option.labelShort}
              isSelected={this.state.filter.oil === option.value}
              onClick={() => { this.handleFilterClick(option.value) }
              } />))}
        </div>
        <div className='tableType'>
          <div className={`item ${this.state.filter.type === 0 ? 'selected' : ''}`} onClick={() => this.handleTableTypeChange(0)}>Hinna ajalugu</div>
          <div className={`item ${this.state.filter.type === 1 ? 'selected' : ''}`} onClick={() => this.handleTableTypeChange(1)}>Hinna efektiivsus</div>
        </div>
        {/**Hinna ajalugu */}
        {this.state.filter.type === 0 && (
          <>
            {filteredResults.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <div className='scrollContainer' style={{ height: "400px", marginTop: '15px', width: '700px' }}>
                  <ResponsiveContainer width="100%" height="100%" minWidth="540px" >
                    <LineChart
                      width={540}
                      height={300}
                      data={filteredResults}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="endDatetime"
                        scale="time"
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(date) => moment(date).format('DD.MM.YYYY')}
                        tick={{ dy: 10 }}
                        fontSize={14}
                      />
                      <YAxis yAxisId="left" fontSize={14}>
                        <Label value="Kogus" angle={-90} position="insideLeft" offset={-10} fontSize={15} />
                      </YAxis>
                      <YAxis yAxisId="right" orientation="right" fontSize={14}>
                        <Label fontSize={15} value="Hind" angle={-90} position="insideLeft" offset={60} />
                      </YAxis>
                      <Tooltip
                        labelStyle={{ fontSize: '14px' }}
                        contentStyle={{ fontSize: '14px' }}
                        formatter={(value, name, entry) => [getNumberDisplayValue(value, 3), name]}
                        labelFormatter={(label) => moment(label).format('DD.MM.YYYY')}
                      />
                      <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ paddingTop: 10, fontSize: '15px' }}
                      />
                      <Line yAxisId="left" type="monotone" dataKey="amount" stroke="#0d3a88" activeDot={{ r: 8 }} name='Kogus (l)' />
                      <Line yAxisId="right" type="monotone" dataKey="purchasePrice" stroke="#5F8D4E" name="Hind (€)" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className='scrollContainer'>
                  <table className="table-container">
                    <thead>
                      <tr>
                        <th>Nr</th>
                        <th>Kuupäev</th>
                        <th>Tüüp</th>
                        <th>Hind (€/l)</th>
                        <th>Tarnepiirkond</th>
                        <th>Kogus (l)</th>
                        <th>Tarnija</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredResults.map((row, index) => (
                        <tr key={index}>
                          <td className='link' title='Ava hange'>
                            <Link to={`/oil-requests/${row.oilRequestId}`}>
                              {row.oilRequestId}
                            </Link>
                          </td>
                          <td>{moment(row.endDatetime).format('DD.MM.YYYY')}</td>
                          <td>{getNameForOil(row.type)}</td>
                          <td>{getNumberDisplayValue(row.purchasePrice, 3)}</td>
                          <td>{row.county}</td>
                          <td>{getNumberDisplayValue(row.amount, 3)}</td>
                          <td>{row.bidder}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '14px', fontStyle: 'italic' }}>Andmed puuduvad</div>
            )}
          </>
        )}
        {/**Hinna efektiivsus */}
        {this.state.filter.type === 1 && (
          <>
            {filteredResults.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <div className='scrollContainer' style={{ height: "400px", marginTop: '15px' }}>
                  <ResponsiveContainer width="100%" height="100%" minWidth="540px" >
                    <LineChart
                      width={540}
                      height={300}
                      data={filteredResults}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="endDatetime"
                        scale="time"
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(date) => moment(date).format('DD.MM.YYYY ')}
                        tick={{ dy: 10 }}
                        fontSize={14}

                      />
                      <YAxis yAxisId="left" fontSize={14}>
                        <Label value="Hind" angle={-90} position="insideLeft" offset={10} fontSize={15} />
                      </YAxis>
                      <Tooltip
                        labelStyle={{ fontSize: '14px' }}
                        contentStyle={{ fontSize: '14px' }}
                        formatter={(value, name, entry) => [getNumberDisplayValue(value, 3), name]}
                        labelFormatter={(label) => moment(label).format('DD.MM.YYYY')}
                      />
                      <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ paddingTop: 10, fontSize: '15px' }}
                      />
                      <Line yAxisId="left" type="monotone" dataKey="purchasePrice" stroke="#0d3a88" name="Ostuhind (€/l)" />
                      <Line yAxisId="left" type="monotone" dataKey="bestPricePerOilRequest" stroke="#5F8D4E" name="Parim hind hankes (€/l)" />
                      <Line yAxisId="left" type="monotone" dataKey="avgPricePerOilRequest" stroke="#DB9E21" name="Keskmine hind hankes (€/l)" />
                      <Line yAxisId="left" type="monotone" dataKey="outerPrice" stroke="#CF0000" name="Portaaliväline hind (€/l)" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className='scrollContainer'>
                  <table className="table-container">
                    <thead>
                      <tr>
                        <th>Nr</th>
                        <th>Kuupäev</th>
                        <th>Tüüp</th>
                        <th>Ostuhind (€/l)</th>
                        <th>Portaaliväline hind (€/l)</th>
                        <th>Kogus (l)</th>
                        <th>Sääst (€)</th>
                        <th>Efektiivsus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredResults.map((row) => (
                        <tr key={row.id}>
                          <td className='link' title='Ava hange'>
                            <Link to={`/oil-requests/${row.oilRequestId}`}>
                              {row.oilRequestId}
                            </Link>
                          </td>
                          <td>{moment(row.endDatetime).format('DD.MM.YYYY')}</td>
                          <td>{getNameForOil(row.type)}</td>
                          <td>{getNumberDisplayValue(row.purchasePrice, 3)}</td>
                          <td>
                            <InputGroup>
                              <Form.Control
                                style={{ fontSize: '14px' }}
                                type="number"
                                value={row.outerPrice}
                                onChange={(e) => { this.handleInputChange(e, row) }}
                                name="outerPrice"
                              />
                            </InputGroup>
                          </td>
                          <td>{row.amount}</td>
                          <td>{row.outerPrice && row.purchasePrice && row.amount ? getNumberDisplayValue((row.outerPrice - row.purchasePrice) * row.amount, 2) : 0}</td>
                          <td>{row.outerPrice && <Effectiveness percentage={((row.outerPrice - row.purchasePrice) / row.outerPrice * 100)} />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '14px', fontStyle: 'italic' }}>Andmed puuduvad</div>
            )}
          </>
        )}

      </div>
    );
  }
}
